import { encryptECIES } from "@/lib/crypto/escrow";
import { encryptWithKey, generateRandomBytes } from "@/lib/crypto/masterKey";
import { elgamalWrap, scalarSplit, vaultKeygen } from "@/lib/crypto/threshold";
import { useVaultStore } from "@/stores/vault-store";
import {
  createTrusteeShare,
  deleteTrusteeShare,
  getAdviserEscrowKey,
  getVaultUnlockConfig,
  listTrusteeShares,
  setTrusteeExecutor,
  setVaultUnlockConfig,
} from "@secure-vault/api";
import { ShareItem } from "@secure-vault/api/types";
import { Button, ConfirmDialog, Input, Label, Slider } from "@secure-vault/ui";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle,
  Key,
  Loader2,
  ShieldAlert,
  Sparkles,
  Trash2,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { UnlockVaultPanel } from "./unlock-vault-panel";

export function VaultTrusteesContainer() {
  const { isUnlocked, masterKey } = useVaultStore();

  const [shares, setShares] = useState<ShareItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTrusteeEmail, setNewTrusteeEmail] = useState("");
  const [threshold, setThreshold] = useState<number>(2);
  const [executorEmail, setExecutorEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Animation states
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitCompleted, setSplitCompleted] = useState(false);

  const [shareToDelete, setShareToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!isUnlocked) return;
    // On mount: load shares first, then hydrate the quorum threshold from the
    // owner's persisted unlock config so the slider reflects the stored value.
    void (async () => {
      const shareList = await loadShares();
      await loadStoredThreshold(shareList.length);
    })();
  }, [isUnlocked]);

  const loadShares = async (): Promise<ShareItem[]> => {
    setLoading(true);
    try {
      const shareList = await listTrusteeShares("all");
      setShares(shareList);

      // Do NOT reset the threshold here — loadShares also runs after every
      // add/remove/distribute, and clobbering it was why the Quorum Threshold
      // control lost the owner's setting. Only clamp it into the range the new
      // trustee count allows: at least 2, at most the number of trustees.
      const maxThreshold = Math.max(2, shareList.length);
      setThreshold((t) => Math.min(Math.max(t, 2), maxThreshold));

      const executor = shareList.find((s) => s.is_executor);
      setExecutorEmail(executor ? executor.trustee_identifier : null);
      return shareList;
    } catch (err) {
      console.error("Failed to load trustee shares:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Hydrate the quorum threshold from the persisted unlock config (the source of
  // truth). Runs once on mount; a fresh vault with no stored config keeps the
  // default. Clamped to the current trustee count as a safety net.
  const loadStoredThreshold = async (trusteeCount: number) => {
    try {
      const config = await getVaultUnlockConfig();
      if (config.configured && config.threshold) {
        const maxThreshold = Math.max(2, trusteeCount);
        setThreshold(Math.min(Math.max(config.threshold, 2), maxThreshold));
      }
    } catch (err) {
      console.error("Failed to load stored quorum threshold:", err);
    }
  };

  const handleAddTrustee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTrusteeEmail || !masterKey) return;

    setError(null);
    const email = newTrusteeEmail.trim().toLowerCase();

    // Basic email check
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (shares.some((s) => s.trustee_identifier === email)) {
      setError("This trustee email has already been added.");
      return;
    }

    setLoading(true);

    try {
      // Nomination only records the trustee. Real cryptographic material (the Shamir shard
      // locked with a Trustee_Secret, and that secret locked to the adviser key) is written
      // when the owner clicks "Split & Distribute". We persist a placeholder so the trustee
      // appears in the roster; the backend requires non-empty blobs.
      const makeExecutor = shares.length === 0; // first trustee defaults to Executor
      await createTrusteeShare({
        share_index: shares.length + 1,
        encrypted_shard: "PENDING",
        encrypted_trustee_secret: "PENDING",
        is_executor: makeExecutor,
        trustee_identifier: email,
      });

      if (makeExecutor) setExecutorEmail(email);
      setNewTrusteeEmail("");
      loadShares();
    } catch (err: unknown) {
      console.error("Failed to add trustee:", err);
      setError(
        "We couldn't add this trustee. Please try again. If the problem continues, contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMakeExecutor = async (share: ShareItem) => {
    // Persist the executor change to the backend. Previously this only updated
    // local state, so the designation was lost on reload (and only ever written
    // during "Split & Distribute"). Optimistically reflect it, revert on failure.
    const previous = executorEmail;
    setError(null);
    setExecutorEmail(share.trustee_identifier);
    try {
      await setTrusteeExecutor(share.share_id);
      loadShares();
    } catch (err: unknown) {
      console.error("Failed to set executor:", err);
      setExecutorEmail(previous);
      setError(
        (err as Error & { error?: string })?.message ||
          (err as Error & { error?: string })?.error ||
          "Failed to update the executor. Please try again."
      );
    }
  };

  const handleDeleteShare = (shareId: string) => {
    setShareToDelete(shareId);
  };

  const handleConfirmDeleteShare = async () => {
    if (!shareToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteTrusteeShare(shareToDelete);
      setShareToDelete(null);
      loadShares();
    } catch (err) {
      console.error("Failed to delete trustee share:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Performs the real Trustee_Secret distribution and threshold-ElGamal setup.
  // Accepts an optional committed threshold (e.g. from the slider's
  // onValueCommit) so we use the just-released value directly rather than
  // relying on the `threshold` state, which may not have flushed yet.
  const handleApplySSS = async (overrideThreshold?: number) => {
    if (!masterKey || shares.length === 0) return;

    const effectiveThreshold = overrideThreshold ?? threshold;

    setIsSplitting(true);
    setSplitCompleted(false);
    setError(null);

    try {
      // 1. Fetch the adviser's escrow public key — every Trustee_Secret is locked to it.
      const adviserPublicKey = await getAdviserEscrowKey();
      if (!adviserPublicKey) {
        throw new Error(
          "Your adviser needs to finish their part before you can save this recovery setup. Please contact them for help."
        );
      }

      // 2. Generate the vault keypair and wrap the master key under it (hashed-ElGamal).
      const { secretScalar, publicKey } = await vaultKeygen();
      const { c1, c2 } = await elgamalWrap(masterKey, publicKey);

      // 3. Store the unlock ciphertext + reconstruction threshold on the platform
      //    (server holds ciphertext only). Persisting `threshold` here makes it the
      //    single source of truth: the adviser's unlock-initiation step reads it
      //    server-side instead of guessing, so a t-of-N vault always unlocks at t.
      await setVaultUnlockConfig({
        vault_pubkey: publicKey,
        c1,
        c2,
        threshold: effectiveThreshold,
      });

      // 4. Shamir-share the vault secret scalar in the scalar field.
      const scalarShards = await scalarSplit(
        secretScalar,
        effectiveThreshold,
        shares.length
      );

      // 5. For each trustee: lock the shard with a fresh Trustee_Secret, then lock that
      //    secret to the adviser's public key. Re-create the share with real material.
      for (let i = 0; i < shares.length; i++) {
        const trustee = shares[i];
        const shard = scalarShards[i];

        const trusteeSecret = generateRandomBytes(32);
        // Encrypt the base64url scalar string so the trustee recovers it verbatim.
        const encryptedShard = await encryptWithKey(
          new TextEncoder().encode(shard.shard),
          trusteeSecret
        );
        const encryptedTrusteeSecret = await encryptECIES(
          trusteeSecret,
          adviserPublicKey
        );

        await deleteTrusteeShare(trustee.share_id);
        await createTrusteeShare({
          share_index: shard.index,
          encrypted_shard: encryptedShard,
          encrypted_trustee_secret: encryptedTrusteeSecret,
          is_executor:
            trustee.trustee_identifier === executorEmail || trustee.is_executor,
          trustee_identifier: trustee.trustee_identifier,
        });
      }

      // 6. Complete animation
      setTimeout(() => {
        setIsSplitting(false);
        setSplitCompleted(true);
        loadShares();
      }, 1500);
    } catch (err: unknown) {
      console.error("Failed to apply secret sharing parameters:", err);
      setError(
        err instanceof Error && err.message.startsWith("Your adviser needs")
          ? err.message
          : "We couldn't save your recovery setup. Please try again. If the problem continues, contact support."
      );
      setIsSplitting(false);
    }
  };

  if (!isUnlocked) {
    return <UnlockVaultPanel />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="space-y-0.5">
        <h1 className="text-2xl font-bold tracking-tight">
          Plan how your vault can be opened
        </h1>
        <p className="text-muted-foreground text-sm">
          Your trusted people cannot access your vault while you are managing
          it. They can only help open it if your adviser confirms that you have
          died or can no longer manage your affairs.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Nominations Form */}
        <div className="space-y-6 lg:col-span-2">
          {/* Add Trustee */}
          <div className="bg-card space-y-4 rounded-xl border p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-bold">
              <UserPlus className="text-primary h-5 w-5" />
              Add a trustee
            </h2>
            <form
              onSubmit={handleAddTrustee}
              className="flex flex-col gap-3 sm:flex-row"
            >
              <div className="flex-1 space-y-1">
                <Label htmlFor="trustee-email" className="sr-only">
                  Trustee's email address
                </Label>
                <Input
                  id="trustee-email"
                  type="email"
                  value={newTrusteeEmail}
                  onChange={(e) => setNewTrusteeEmail(e.target.value)}
                  placeholder="Enter their email address"
                  required
                  disabled={loading}
                />
              </div>
              <Button type="submit" disabled={loading || !newTrusteeEmail}>
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Add trustee
              </Button>
            </form>

            <p className="text-muted-foreground text-xs leading-relaxed">
              The executor coordinates the trustees if your vault needs to be
              opened. Your first trustee will be selected automatically, and you
              can choose someone else below.
            </p>

            {error && (
              <div className="border-destructive/20 bg-destructive/10 text-destructive flex items-center gap-2.5 rounded-lg border p-3 text-sm">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Active Trustees list */}
          <div className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm">
            <div className="bg-muted/30 border-b p-6">
              <h2 className="flex items-center gap-2 text-base font-bold">
                <Users className="text-primary h-5 w-5" />
                Your trustees ({shares.length})
              </h2>
            </div>

            {loading && shares.length === 0 ? (
              <div className="flex justify-center py-12">
                <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
              </div>
            ) : shares.length === 0 ? (
              <div className="text-muted-foreground space-y-1 p-8 text-center text-sm">
                <p>You haven't added any trustees yet.</p>
                <p className="text-muted-foreground/80 text-xs">
                  Add at least two people you trust to begin your recovery plan.
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {shares.map((t, idx) => (
                  <div
                    key={t.share_id}
                    className="hover:bg-muted/50 flex items-center justify-between p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-muted text-muted-foreground flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="flex items-center gap-2 text-sm font-medium">
                          {t.trustee_identifier}
                          {t.trustee_identifier === executorEmail ? (
                            <span className="text-primary bg-primary/10 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                              Executor
                            </span>
                          ) : null}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Added to your recovery plan
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {t.trustee_identifier !== executorEmail ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-primary h-7 px-2 text-xs"
                          onClick={() => handleMakeExecutor(t)}
                        >
                          Choose as executor
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteShare(t.share_id)}
                      >
                        <Trash2 className="h-4.5 w-4.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Shamir Parameters Slider */}
        <div className="space-y-6">
          <div className="bg-card space-y-6 rounded-xl border p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-base font-bold">
              <Key className="text-primary h-5 w-5" />
              Access requirements
            </h2>

            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground font-medium">
                  Trustees required
                </span>
                <span className="text-primary font-bold">
                  {shares.length > 0 &&
                    `${threshold} of ${shares.length || "?"}`}
                </span>
              </div>

              <Slider
                min={2}
                max={Math.max(2, shares.length)}
                step={1}
                value={[threshold]}
                onValueChange={(val) => setThreshold(val[0])}
                // Changing the quorum threshold auto-splits & redistributes the
                // keys. onValueCommit fires once when the drag/keyboard change
                // settles, so the heavy crypto runs on the final value only —
                // not on every intermediate tick during a drag.
                onValueCommit={(val) => {
                  setThreshold(val[0]);
                  void handleApplySSS(val[0]);
                }}
                disabled={shares.length < 2 || isSplitting}
                className="py-2"
              />

              <p className="text-muted-foreground text-xs leading-relaxed">
                At least <b>{threshold}</b> of your trustees will need to take
                part before your vault can be opened.
              </p>
            </div>

            <Button
              onClick={() => handleApplySSS()}
              disabled={shares.length === 0 || isSplitting}
              className="w-full"
            >
              {isSplitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving your recovery setup...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Save recovery setup
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Key Splitting Interactive CSS Particles / Animation Overlay */}
      <AnimatePresence>
        {isSplitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-background/90 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
          >
            <div className="max-w-lg space-y-8 p-6 text-center">
              <div className="relative mx-auto flex h-36 w-36 items-center justify-center">
                {/* Glowing Core */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1], rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 3 }}
                  className="border-primary/30 bg-primary shadow-primary/30 absolute flex h-16 w-16 items-center justify-center rounded-full border shadow-2xl"
                >
                  <Key className="text-primary-foreground h-6 w-6 font-bold" />
                </motion.div>

                {/* Orbital particles */}
                {Array.from({ length: shares.length }).map((_, i) => {
                  const angle = (i * 360) / shares.length;
                  return (
                    <motion.div
                      key={i}
                      initial={{ scale: 0.2, opacity: 0 }}
                      animate={{
                        x: [0, Math.cos((angle * Math.PI) / 180) * 90],
                        y: [0, Math.sin((angle * Math.PI) / 180) * 90],
                        scale: [0.2, 1, 0.4],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.5,
                        delay: i * 0.15,
                        ease: "easeOut",
                      }}
                      className="bg-primary absolute h-4.5 w-4.5 rounded-full shadow-sm"
                    />
                  );
                })}
              </div>

              <div className="space-y-2">
                <h3 className="flex items-center justify-center gap-2 text-lg font-bold">
                  <Sparkles className="text-primary h-5 w-5 animate-spin" />
                  Preparing your recovery setup
                </h3>
                <p className="text-muted-foreground mx-auto max-w-sm text-xs leading-relaxed">
                  Safely updating your arrangements. This may take a moment.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Notification */}
      <AnimatePresence>
        {splitCompleted && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="bg-card fixed right-6 bottom-6 z-50 flex items-center gap-3 rounded-xl border border-green-500/20 p-4 shadow-lg backdrop-blur-md"
          >
            <CheckCircle className="h-5 w-5 text-green-500" />
            <div className="text-xs">
              <p className="font-semibold">Recovery setup saved</p>
              <p className="text-muted-foreground">
                Your trustees and access requirements are up to date.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground h-7 px-2"
              onClick={() => setSplitCompleted(false)}
            >
              Close
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={shareToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setShareToDelete(null);
        }}
        title="Remove this trustee?"
        description="They will no longer be included in your recovery arrangements."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        destructive
        loading={deleteLoading}
        disabled={deleteLoading}
        onConfirm={handleConfirmDeleteShare}
        onCancel={() => setShareToDelete(null)}
      />
    </div>
  );
}
